import { Empty, Spin } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  DocumentPreview,
  isImageDocument,
  isPdfDocument,
  RolodexDocumentEntry,
  useDocumentSignedUrls,
  useLinkedDocuments,
} from 'features/files'
import { InteractiveCropViewer } from 'features/libra/stepComponents/zoneAnalysis/components/InteractiveCropViewer'
import { SmartImageViewer } from 'features/libra/stepComponents/zoneAnalysis/components/SmartImageViewer'
import { useSelectedOrgAndSiteInterface } from 'features/orgAndSiteSourceOfTruth/orgSiteInterface'
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { DocumentFileFormat } from 'routes/document-viewer/-searchParams'
import styled from 'styled-components'

import { buildDocumentViewerUrl } from './documentViewerUrl'
import { useLibraDocument } from './LibraDocumentContext'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.forecast.background1};
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const EmptyStateDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const FULL_HEIGHT_STYLE: React.CSSProperties = { height: '100%' }

const ViewerWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  min-height: 0;
`

export const EmbeddedDocumentPanel: FC = () => {
  const {
    selectedDocumentId,
    unitId,
    cropMode,
    zoneDrawingMode,
    citationInitialPage,
    setCurrentViewPage,
    navigationKey,
  } = useLibraDocument()
  const documentPreviewRef = useRef<{ scrollToPage: (page: number) => void }>(
    null
  )

  useEffect(() => {
    if (navigationKey > 0) {
      documentPreviewRef.current?.scrollToPage(citationInitialPage)
    }
  }, [navigationKey, citationInitialPage])
  const { ref: layoutRef, height } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 200,
  })
  const { rolodexPatch } = useRolodexEdit()

  const { documents, isLoading, forceRefresh } = useLinkedDocuments(
    useMemo(() => (unitId ? { unit: unitId } : undefined), [unitId])
  )

  const selectedDocument = useMemo<RolodexDocumentEntry | null>(() => {
    if (!selectedDocumentId) return null
    return documents.find((doc) => doc.entry_id === selectedDocumentId) ?? null
  }, [documents, selectedDocumentId])

  const isCropMode =
    cropMode.enabled && selectedDocumentId === cropMode.sourceDocumentId

  const isZoneManagementMode =
    zoneDrawingMode.enabled &&
    selectedDocumentId === zoneDrawingMode.zoneReferenceImageId

  const { signedUrl: selectedDocumentUrl, filePath: selectedDocumentFilePath } =
    useDocumentSignedUrls(selectedDocument)

  const interactiveUrl =
    isZoneManagementMode || isCropMode ? selectedDocumentUrl : undefined

  const { getOrganizationId, getSiteId } = useSelectedOrgAndSiteInterface()

  const handleRotatePage = useCallback(
    async (page: number, newRotation: number) => {
      if (!selectedDocument) return
      const existing = selectedDocument.pageRotations ?? {}
      const updated = { ...existing, [String(page)]: newRotation }
      await rolodexPatch([
        { id: selectedDocument.entry_id, data: { pageRotations: updated } },
      ])
      await forceRefresh()
    },
    [selectedDocument, rolodexPatch, forceRefresh]
  )

  const handleRotateAll = useCallback(
    async (angle: number, numPages: number) => {
      if (!selectedDocument) return
      const existing = selectedDocument.pageRotations ?? {}
      const updated: Record<string, number> = {}
      for (let p = 1; p <= numPages; p++) {
        const current = existing[String(p)] ?? 0
        updated[String(p)] = (current + angle) % 360
      }
      await rolodexPatch([
        { id: selectedDocument.entry_id, data: { pageRotations: updated } },
      ])
      await forceRefresh()
    },
    [selectedDocument, rolodexPatch, forceRefresh]
  )

  const handleOpenInNewTab = useCallback(() => {
    if (!selectedDocument || !selectedDocumentFilePath) return
    const fileFormat = isPdfDocument(selectedDocument)
      ? DocumentFileFormat.PDF
      : isImageDocument(selectedDocument)
        ? DocumentFileFormat.Image
        : undefined

    const url = buildDocumentViewerUrl({
      entryId: selectedDocument.entry_id,
      filePath: selectedDocumentFilePath,
      title:
        selectedDocument.filename ||
        selectedDocument.visible_name ||
        'Document',
      fileFormat,
      orgId: getOrganizationId() ?? undefined,
      siteId: getSiteId() ?? undefined,
      pageRotations: selectedDocument.pageRotations,
    })

    window.open(url, '_blank', `popup=yes`)
  }, [selectedDocument, selectedDocumentFilePath, getOrganizationId, getSiteId])

  if (isLoading) {
    return (
      <PanelContainer>
        <EmptyStateContainer>
          <Spin size='large' />
        </EmptyStateContainer>
      </PanelContainer>
    )
  }

  if (documents.length === 0) {
    return (
      <PanelContainer>
        <EmptyStateContainer>
          <Empty
            description={
              <>
                <EmptyStateTitle>No documents available</EmptyStateTitle>
                <EmptyStateDescription>
                  Upload documents in the Data Health step
                </EmptyStateDescription>
              </>
            }
          />
        </EmptyStateContainer>
      </PanelContainer>
    )
  }

  return (
    <PanelContainer ref={layoutRef}>
      {selectedDocument ? (
        <ViewerWrapper>
          {isCropMode && cropMode.onCropComplete && interactiveUrl ? (
            <InteractiveCropViewer
              url={interactiveUrl}
              isPdf={isPdfDocument(selectedDocument)}
              height={height ? height - 20 : 1000}
              currentPage={cropMode.currentPage}
              pageRotations={selectedDocument.pageRotations}
              onCropChange={(rect) => {
                cropMode.onCropComplete?.(rect)
              }}
              onPageChange={cropMode.onPageChange}
            />
          ) : isZoneManagementMode && interactiveUrl ? (
            <SmartImageViewer
              url={interactiveUrl}
              documentId={selectedDocument.entry_id}
              height={height ? height - 20 : 1000}
              enableZoneDrawing={zoneDrawingMode.isDrawingMode || false}
              onZoneDrawn={zoneDrawingMode.onZoneDrawn}
              onNaturalSize={zoneDrawingMode.onImageNaturalSize}
              liveZones={zoneDrawingMode.zones}
            />
          ) : (
            <DocumentPreview
              ref={documentPreviewRef}
              document={selectedDocument}
              title={selectedDocument.filename || selectedDocument.visible_name}
              height={height ? height - 20 : undefined}
              containerStyle={FULL_HEIGHT_STYLE}
              continuousScroll={true}
              virtualize={true}
              onPageChange={setCurrentViewPage}
              onOpenInNewTab={
                selectedDocumentFilePath ? handleOpenInNewTab : undefined
              }
              onRotatePage={handleRotatePage}
              onRotateAll={handleRotateAll}
            />
          )}
        </ViewerWrapper>
      ) : (
        <EmptyStateContainer>
          <Empty
            description={
              <>
                <EmptyStateTitle>No document selected</EmptyStateTitle>
                <EmptyStateDescription>
                  Select a document from the navigator to preview
                </EmptyStateDescription>
              </>
            }
          />
        </EmptyStateContainer>
      )}
    </PanelContainer>
  )
}
